<template>
    <section class="flight-booking-section" v-if="travelers.length > 0">
        <div class="content-panel">
            <div class="panel-title">
                <h3>{{ $t('allPassengersContactInformation') }}</h3>
            </div>
            <div class="panel-body">
                <div class="two-col-form">
                    <div class="form-group">
                        <label for="">{{ $t('emailAddress') }}</label>
                        <input type="text" :name="$t('emailAddress')" ref="email" v-validate="'required|email'" v-model="contactInfo.emailAddress" />
                        <span class="invalid">{{ errors.first($t('emailAddress')) }}</span>
                    </div>

                    <div class="form-group">
                        <label for="">{{ $t('phoneNumber') }}</label>
                        <div class="phone-group">
                            <v-select 
                                :options="sortedCountryList" 
                                v-model="contactInfo.countryInfo" 
                                class="country-code"
                                :getOptionLabel="country => '+ ' + country.dialCode" 
                                :reduce="country => { return {dialCode: country.dialCode, code: country.code}}" 
                                name="DialCode">
                            </v-select>
                            <input type="text" class="number" :name="$t('phoneNumber')" v-validate="'required|numeric'" v-model="contactInfo.phoneNumber" />
                        </div>
                        <span class="invalid">{{ errors.first($t('phoneNumber')) }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="content-panel" v-for="(traveler, index) in travelers" :key="index">
            <div class="panel-title">
                <h3 class="passenger-panel-title">
                    <span v-if="traveler.type === 'child'">{{ $t('childPassenger') }}</span>
                    <span v-if="traveler.type === 'infant'">{{ $t('infantPassenger') }}</span>
                    <span v-if="traveler.type === 'adult'">{{ $t('adultPassenger') }}</span>
                </h3>
            </div>
            <div class="panel-body">
                <div class="two-col-form">
                    <div class="form-group">
                        <label :for="'firstName' + index">{{ $t('firstName') }}</label>
                        <input type="text" :id="'firstName' + index" :name="$t('firstNameForPassenger') + ' ' + (index + 1)" v-validate="'required|alpha_spaces'" v-model="traveler.firstName" />
                        <span class="invalid">{{ errors.first($t('firstNameForPassenger') + ' '  + (index + 1)) }}</span>
                    </div>

                    <div class="form-group">
                        <label :for="'lastName' + index">{{ $t('lastName') }}</label>
                        <input type="text" :id="'lastName' + index" :name="$t('lastNameForPassenger') + ' ' + (index + 1)" v-validate="'required|alpha_spaces'" v-model="traveler.lastName" />
                        <span class="invalid">{{ errors.first($t('lastNameForPassenger') + ' ' + (index + 1)) }}</span>
                    </div>
                    <div class="radio-group">
                        <div class="label">{{ $t('gender') }}</div>
                        <div class="radio">
                            <input type="radio" v-model="traveler.gender" :name="$t('genderForPassenger') + ' ' + (index + 1)" v-validate="'required'" :id="'mTraveler' + index" value="MALE" />
                            <label :for="'mTraveler' + index">{{ $t('male') }}</label>
                        </div>
                        <div class="radio">
                            <input type="radio" v-model="traveler.gender" :name="$t('genderForPassenger') + ' ' + (index + 1)" v-validate="'required'" :id="'fTraveler' + index" value="FEMALE" />
                            <label :for="'fTraveler' + index">{{ $t('female') }}</label>
                        </div>
                        <span class="invalid">{{ errors.first($t('genderForPassenger') + ' '  + (index + 1)) }}</span>
                    </div>

                    <div class="form-group">
                        <label :for="$t('dateOfBirthForPassenger') + (index + 1)">{{ $t('birthday') }}</label>

                        <date-selector
                            v-model="traveler.dob"
                            :min="(traveler.type === 'infant' ? infantMinDate : (traveler.type === 'child' ? childMinDate : adultMinDate))"
                            :max="(traveler.type === 'infant' ? infantMaxDate : (traveler.type === 'child' ? infantMinDate : childMinDate))"
                            v-validate="'required|date_format:yyyy-mm-dd'"
                            :name="$t('dateOfBirthForPassenger') + ' ' + (index + 1)"
                            :id="$t('dateOfBirthForPassenger') + (index + 1)">
                        </date-selector>
                        <span class="invalid">{{ errors.first($t('dateOfBirthForPassenger') + ' ' + (index + 1)) }}</span>
                    </div>

                    <div class="form-group" v-if="travelerDataRequirements && travelerDataRequirements.includes('PASSPORT_NUMBER')">
                        <label :for="$t('passportNumberForPassenger') + (index + 1)">{{ $t('passportNumber') }}</label>
                        <input 
                            type="text" 
                            :name="$t('passportNumberForPassenger') + (index + 1)" 
                            v-validate="'required'" 
                            v-model="traveler.passportNumber"
                            :id="$t('passportNumberForPassenger') + (index + 1)" />

                        <span class="invalid">{{ errors.first($t('passportNumberForPassenger') + ' '  + (index + 1)) }}</span>
                    </div>

                    <div class="form-group" v-if="travelerDataRequirements && travelerDataRequirements.includes('PASSPORT_ISSUING_COUNTRY')">
                        <label :for="$t('passportIssuingCountryForPassenger') + (index + 1)">{{ $t('passportIssuingCountry') }}</label>
                        <div>
                            <v-select
                                :options="sortedCountryList.filter(country => country.code)"
                                v-model="traveler.nationality"
                                class="country-code"
                                :getOptionLabel="country => country.name"
                                :reduce="country => country.code"
                                :name="$t('passportIssuingCountryForPassenger') + ' ' + (index + 1)"
                                v-validate="'required'"
                                :id="$t('passportIssuingCountryForPassenger') + (index + 1)"
                            ></v-select>
                        </div>
                        <span class="invalid">{{ errors.first($t('passportIssuingCountryForPassenger') + ' ' + (index + 1)) }}</span>
                    </div>

                    <div class="form-group" v-if="travelerDataRequirements && travelerDataRequirements.includes('PASSPORT_ISSUING_CITY')">
                        <label :for="$t('passportIssuingCityForPassenger') + (index + 1)">{{ $t('passportIssuingCity') }}</label>
                        <input 
                            type="text" 
                            :name="$t('passportIssuingCityForPassenger') + ' ' + (index + 1)" 
                            v-validate="'required'" 
                            v-model="traveler.passportIssuingCity"
                            :id="$t('passportIssuingCityForPassenger') + (index + 1)" />
                        <span class="invalid">{{ errors.first($t('passportIssuingCityForPassenger') + ' ' + (index + 1)) }}</span>
                    </div>

                    <div class="form-group" v-if="travelerDataRequirements && travelerDataRequirements.includes('PASSPORT_ISSUING_DATE')">
                        <label :for="'passportIssueDate' + index">{{ $t('passportIssuingDate') }}</label>

                        <date-picker
                            :max-date="new Date()"
                            :min-date="passportIssuingMinDate"
                            v-model="traveler.passportIssueDate"
                            v-validate="'required'"
                            :id="'passportIssueDate' + index"
                            :name="$t('passportIssuingDateForPassenger') + ' ' + (index + 1)"
                            :input-props='{
                                class: "date-picker",
                                placeholder: "Select Date",
                                readonly: true,
                            }'
                            :popover='{
                                visibility: "click"
                            }'
                            value="null">
                        </date-picker>
                        <span class="invalid">{{ errors.first($t('passportIssuingDateForPassenger') + ' ' + (index + 1)) }}</span>

                    </div>
                    <div class="form-group" v-if="travelerDataRequirements && travelerDataRequirements.includes('PASSPORT_EXPIRY_DATE')">
                        <label for="">{{ $t('passportExpiryDate') }}</label>
                        <date-picker
                            :min-date="new Date()"
                            :max-date="passportExpiryMaxDate"
                            v-model="traveler.passportExpireDate"
                            v-validate="'required'"
                            :id="'passportIssueDate' + index"
                            :name="$t('passportExpiryDateForPassenger') + ' ' + (index + 1)"
                            :input-props='{
                                class: "date-picker",
                                placeholder: "Select Date",
                                readonly: true,
                            }'
                            :popover='{
                                visibility: "click"
                            }'
                            value="null">
                        </date-picker>
                        <span class="invalid">{{ errors.first($t('passportExpiryDateForPassenger') + ' ' + (index + 1)) }}</span>
                    </div>

                </div>
            </div>
        </div>
        <div class="book-cta">
            <a class="back" @click="$router.go(-1)"><i class="fal fa-caret-left"></i> {{ $t('backToAllResults') }}</a>
            <button class="btn btn-lg" v-if="!buttonLoader" @click="submitPassengerForm()">
                <span>{{ $t('continue') }}</span>
            </button>
            <button disabled="disabled" class="btn btn-sec btn-lg progress" v-else>
                <span><i class="fal fa-spinner fa-pulse"></i></span>
            </button>
        </div>
    </section>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import { formsMixin } from '@/mixins/formsMixin'
import DateSelector from '@/components/DateSelector';
import DatePicker from 'v-calendar/lib/components/date-picker.umd'
Vue.component('date-picker', DatePicker)
import 'vue-datetime/dist/vue-datetime.css'

export default {
    name: 'FlightBookingPassengers',
    props: {
        contactInfo: Object,
        travelers: Array,
        travelerDataRequirements: Array
    },
    mixins: [formsMixin],
    data () {
        return {
          queryString: this.$route.query,
          stepData: { passengers: this.travelers },
          buttonLoader: false,
          infantMaxDate: new Date()
        }
    },
    components: {
        DateSelector
    },
    methods: {
        submitPassengerForm() {
            let self = this;

            self.$validator.validateAll().then((result) => {
                if (result) {
                    self.buttonLoader = true;

                    self.stepData.passengers[0].email = self.contactInfo.emailAddress;
                    self.stepData.passengers[0].phoneNumber = {
                        country: self.contactInfo.countryInfo.code,
                        countryCallingCode: self.contactInfo.countryInfo.dialCode.toString(),
                        numberWithoutCountryCode: self.contactInfo.phoneNumber
                    };
                    for (let i = 0; i < self.stepData.passengers.length; i++) {
                        let passenger = self.stepData.passengers[i];
                        
                        // debugger;
                        passenger.dateOfBirth = self.$moment(passenger.dob).format("YYYY-MM-DD");
                        passenger.travelerReference = passenger.reference;

                        if (self.travelerDataRequirements) {
                            if (self.travelerDataRequirements.includes('PASSPORT_ISSUING_DATE')) {
                                passenger.passportIssuingDate = self.$moment(passenger.passportIssueDate).format("YYYY-MM-DD");
                            }
                            if (self.travelerDataRequirements.includes('PASSPORT_EXPIRY_DATE')) {
                                passenger.passportExpiryDate = self.$moment(passenger.passportExpireDate).format("YYYY-MM-DD");
                            }
                        }
                    }

                    self.$emit('nextStep', self.stepData);
                }
            });
        },

        removeDuplicates(myArr, prop) {
            return myArr.filter((obj, pos, arr) => {
                return arr.map(mapObj => mapObj[prop]).indexOf(obj[prop]) === pos;
            });
        },

        log($event,i) {
            console.log($event);
            console.log(this.travelers[0].dob);

            // this.stepData.passengers[i] = $event;
        }
    },

    computed: {
        ...mapGetters ([
          'getCountryList'
        ]),

        sortedCountryList() {
            let dialCodesArr = this.removeDuplicates(this.getCountryList, "dialCode");

            let sortedList = dialCodesArr.sort((a, b) => (a.dialCode > b.dialCode) ? 1 : -1);
            return sortedList
        },

        adultMinDate() {
          let self = this;
            return self.$moment().subtract(100, 'years').toDate();
        },

        infantMinDate() {
          let self = this;
            return self.$moment().subtract(2, 'years').toDate();
        },

        childMinDate() {
          let self = this;
            return self.$moment().subtract(11, 'years').toDate();
        },

        passportIssuingMinDate() {
          let self = this;
           return self.$moment().subtract(15, 'years').toDate();
        },

        passportExpiryMaxDate() {
          let self = this;
           return self.$moment().add(15, 'years').toDate();
        }
    }
}
</script>